import { Button } from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import {
  CheckIcon,
  PencilIcon,
  SendIcon,
  XIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import type { ExpenseRecordDetailDTO } from "../api/client";

type ExpenseRecordActionsProps = {
  record: ExpenseRecordDetailDTO;
  canCreate: boolean;
  canUpdate: boolean;
  canApprove: boolean;
  canPay: boolean;
  canVoid: boolean;
  isSubmitting?: boolean;
  isApproving?: boolean;
  isClosing?: boolean;
  isCancelling?: boolean;
  onSubmit: () => void;
  onApprove: () => void;
  onReject: () => void;
  onMarkPaid: () => void;
  onClose: () => void;
  onCancel: () => void;
  onVoid: () => void;
};

export function ExpenseRecordActions({
  record,
  canCreate,
  canUpdate,
  canApprove,
  canPay,
  canVoid,
  isSubmitting,
  isApproving,
  isClosing,
  isCancelling,
  onSubmit,
  onApprove,
  onReject,
  onMarkPaid,
  onClose,
  onCancel,
  onVoid,
}: ExpenseRecordActionsProps) {
  const editable = ["draft", "rejected"].includes(record.status);
  const actions: Array<{
    key: string;
    node: ReactNode;
  }> = [];

  if (editable && canUpdate) {
    actions.push({
      key: "edit",
      node: (
        <Button key="edit" variant="outline" className="w-full justify-start" asChild>
          <Link to="/app/expenses/$id/edit" params={{ id: record.id }}>
            <PencilIcon className="mr-2 size-4" />
            ແກ້ໄຂ
          </Link>
        </Button>
      ),
    });
  }

  if (["draft", "rejected"].includes(record.status) && canCreate) {
    actions.push({
      key: "submit",
      node: (
        <Button
          key="submit"
          className="w-full justify-start"
          disabled={isSubmitting}
          onClick={onSubmit}
        >
          <SendIcon className="mr-2 size-4" />
          ສົ່ງອະນຸມັດ
        </Button>
      ),
    });
  }

  if (record.status === "submitted" && canApprove) {
    actions.push(
      {
        key: "approve",
        node: (
          <Button
            key="approve"
            className="w-full justify-start"
            disabled={isApproving}
            onClick={onApprove}
          >
            <CheckIcon className="mr-2 size-4" />
            ອະນຸມັດ
          </Button>
        ),
      },
      {
        key: "reject",
        node: (
          <Button
            key="reject"
            variant="outline"
            className="w-full justify-start"
            onClick={onReject}
          >
            <XIcon className="mr-2 size-4" />
            ປະຕິເສດ
          </Button>
        ),
      },
    );
  }

  if (record.status === "approved" && canPay) {
    actions.push({
      key: "pay",
      node: (
        <Button key="pay" className="w-full justify-start" onClick={onMarkPaid}>
          ບັນທຶກການຈ່າຍ
        </Button>
      ),
    });
  }

  if (record.status === "paid" && canPay) {
    actions.push({
      key: "close",
      node: (
        <Button
          key="close"
          variant="outline"
          className="w-full justify-start"
          disabled={isClosing}
          onClick={onClose}
        >
          ປິດລາຍການ
        </Button>
      ),
    });
  }

  if (["draft", "submitted", "approved"].includes(record.status) && canCreate) {
    actions.push({
      key: "cancel",
      node: (
        <Button
          key="cancel"
          variant="ghost"
          className="w-full justify-start"
          disabled={isCancelling}
          onClick={onCancel}
        >
          ຍົກເລີກ
        </Button>
      ),
    });
  }

  if (["paid", "closed"].includes(record.status) && canVoid) {
    actions.push({
      key: "void",
      node: (
        <Button
          key="void"
          variant="destructive"
          className="w-full justify-start"
          onClick={onVoid}
        >
          ຍົກເລີກ
        </Button>
      ),
    });
  }

  if (actions.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        ບໍ່ມີການດຳເນີນການທີ່ພ້ອມໃຊ້
      </p>
    );
  }

  return <div className="flex flex-col gap-2">{actions.map((a) => a.node)}</div>;
}
